import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowUpRight, CaretDown, CaretRight, CircleNotch, DownloadSimple, FileText, MagnifyingGlass } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { downloadBlob } from "@/lib/bootcampFiles";
import MarkdownMessage from "@/components/MarkdownMessage";
import BlueprintDownloadButton, { isFullBlueprint } from "@/components/BlueprintDownloadButton";
import { useTheme } from "@/contexts/ThemeContext";

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric" });
};
const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

export function UserChatsTab({ user }) {
  const [all, setAll] = useState(null);
  const [filtered, setFiltered] = useState(null);
  const [query, setQuery] = useState("");
  const q = useDebounced(query.trim(), 350);
  const [openConv, setOpenConv] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [portfolio, setPortfolio] = useState(null);

  useEffect(() => {
    api.get(`/admin/users/${user.id}/conversations`).then(({ data }) => setAll(data)).catch(() => setAll([]));
    api.get(`/admin/users/${user.id}/portfolio`).then(({ data }) => setPortfolio(data)).catch(() => setPortfolio({ gpt_count: 0, gpts: [] }));
  }, [user.id]);
  useEffect(() => {
    if (!q) { setFiltered(null); return; }
    let alive = true;
    api.get(`/admin/users/${user.id}/conversations`, { params: { q } }).then(({ data }) => { if (alive) setFiltered(data); }).catch(() => { if (alive) setFiltered([]); });
    return () => { alive = false; };
  }, [q, user.id]);

  const convs = q ? filtered : all;
  const groups = useMemo(() => {
    const m = new Map();
    (convs || []).forEach((c) => {
      const g = m.get(c.gpt_id) || { gpt_id: c.gpt_id, gpt_name: c.gpt_name, gpt_tab: c.gpt_tab, chats: [], messages: 0, last: "" };
      g.chats.push(c); g.messages += c.message_count; g.last = g.last > (c.last_message_at || "") ? g.last : c.last_message_at || "";
      m.set(c.gpt_id, g);
    });
    return [...m.values()].sort((a, b) => (b.last > a.last ? 1 : -1));
  }, [convs]);

  const downloadPortfolio = async () => {
    setBusy("portfolio"); setError("");
    try {
      const date = new Date().toLocaleDateString("en-GB", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric" }).replace(/ /g, "-");
      await downloadBlob(`/admin/users/${user.id}/portfolio.pdf`, `Portfolio-${(user.name || "Participant").replace(/[^A-Za-z0-9]+/g, "-")}-${date}.pdf`);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || "Portfolio download failed — please retry."); }
    finally { setBusy(""); }
  };

  const download = async (kind, gptId, gptName) => {
    const key = `${kind}-${gptId || "all"}`;
    setBusy(key); setError("");
    try {
      const qs = gptId ? `?gpt_id=${encodeURIComponent(gptId)}` : "";
      await downloadBlob(`/admin/users/${user.id}/transcripts.${kind}${qs}`, `transcripts-${slug(user.email)}${gptId ? `-${slug(gptName)}` : ""}.${kind}`);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || "Download failed"); }
    finally { setBusy(""); }
  };

  if (all === null) return <div className="py-10 flex justify-center" data-testid="drawer-chats-loading"><div className="spinner" /></div>;
  if (openConv) return <TranscriptReader conversation={openConv} onBack={() => setOpenConv(null)} />;

  const totalMsgs = all.reduce((s, c) => s + c.message_count, 0);
  const lastChat = all.reduce((s, c) => (s > (c.last_message_at || "") ? s : c.last_message_at || ""), "");
  const gptCount = new Set(all.map((c) => c.gpt_id)).size;

  return (
    <div className="space-y-4" data-testid="drawer-chats">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm" style={{ color: "var(--muted)" }} data-testid="drawer-chats-summary">
          {all.length} chat{all.length === 1 ? "" : "s"} · {totalMsgs} messages · {gptCount} GPT{gptCount === 1 ? "" : "s"} used{lastChat ? ` · last chat ${fmtDate(lastChat)}` : ""}
        </p>
        {all.length > 0 && (
          <div className="flex gap-1.5">
            <button className="btn btn-ghost btn-sm" onClick={() => download("pdf")} disabled={!!busy} data-testid="drawer-chats-download-pdf"><DownloadSimple size={14} /> {busy === "pdf-all" ? "Preparing…" : "Transcripts (PDF)"}</button>
            <button className="btn btn-ghost btn-sm" onClick={() => download("txt")} disabled={!!busy} data-testid="drawer-chats-download-txt"><FileText size={14} /> {busy === "txt-all" ? "Preparing…" : "Text"}</button>
          </div>
        )}
      </div>
      <div className="portfolio-row" data-testid="drawer-portfolio-row">
        <div className="portfolio-row-text">
          <p className="font-semibold text-sm">Portfolio</p>
          <p className="text-xs" style={{ color: "var(--muted)" }} data-testid="drawer-portfolio-preview">
            {portfolio === null ? "Checking latest work…"
              : portfolio.gpt_count === 0 ? "No GPT work yet"
              : `${portfolio.gpt_count} GPT${portfolio.gpt_count === 1 ? "" : "s"} · latest work from ${portfolio.gpts.map((g) => g.name).join(", ")}`}
          </p>
        </div>
        <button className="pill-cta pill-cta-outline" onClick={downloadPortfolio} disabled={!!busy || !portfolio || portfolio.gpt_count === 0} data-testid="drawer-portfolio-download">
          {busy === "portfolio" ? <CircleNotch size={14} weight="bold" className="spin" /> : <DownloadSimple size={14} weight="bold" />}
          {busy === "portfolio" ? "Preparing…" : "Download portfolio (PDF)"}
        </button>
      </div>
      {error && <p className="error-banner" data-testid="drawer-chats-error">{error}</p>}
      {all.length === 0 ? (
        <p className="text-sm py-6 text-center" style={{ color: "var(--muted)" }} data-testid="drawer-chats-empty">{user.name} hasn’t started any chats yet.</p>
      ) : (
        <>
          <div className="admin-search w-full">
            <MagnifyingGlass size={16} weight="bold" />
            <input placeholder="Search chat titles and message text…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="drawer-chats-search" />
          </div>
          {convs === null ? (
            <div className="py-6 flex justify-center"><div className="spinner" /></div>
          ) : groups.length === 0 ? (
            <p className="text-sm py-6 text-center" style={{ color: "var(--muted)" }} data-testid="drawer-chats-no-results">No chats match “{q}”.</p>
          ) : (
            groups.map((g) => <GptGroup key={g.gpt_id} group={g} forceOpen={!!q} onOpen={setOpenConv} onDownload={download} busy={busy} />)
          )}
        </>
      )}
    </div>
  );
}

function GptGroup({ group, forceOpen, onOpen, onDownload, busy }) {
  const [open, setOpen] = useState(true);
  const expanded = forceOpen || open;
  return (
    <section className="chat-group" data-testid={`drawer-gpt-group-${group.gpt_id}`}>
      <div className="chat-group-head">
        <button className="chat-group-toggle" onClick={() => setOpen((o) => !o)} disabled={forceOpen} style={forceOpen ? { cursor: "default" } : undefined} data-testid={`drawer-gpt-toggle-${group.gpt_id}`}>
          {expanded ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
          <span className="font-semibold">{group.gpt_name}</span>
          <span className="chip chip-muted">{group.gpt_tab}</span>
          <span className="chat-group-meta">{group.chats.length} chat{group.chats.length === 1 ? "" : "s"} · {group.messages} msgs · last {fmtDate(group.last)}</span>
        </button>
        <button className="btn btn-ghost btn-sm" title="Download this GPT's chats (PDF)" disabled={!!busy} onClick={() => onDownload("pdf", group.gpt_id, group.gpt_name)} data-testid={`drawer-gpt-download-${group.gpt_id}`}>
          <DownloadSimple size={13} /> {busy === `pdf-${group.gpt_id}` ? "…" : "PDF"}
        </button>
      </div>
      {expanded && (
        <ul className="chat-list">
          {group.chats.map((c) => (
            <li key={c.id} className="chat-row">
              <button className="chat-row-btn" onClick={() => onOpen(c)} data-testid={`drawer-chat-${c.id}`}>
                <div className="chat-row-meta">
                  <div className="chat-row-title">{c.title}{c.deleted_by_user_at && <span className="chip chip-danger ml-2" data-testid={`drawer-chat-deleted-${c.id}`}>Deleted by participant · {fmtDate(c.deleted_by_user_at)}</span>}</div>
                  <div className="chat-row-sub">
                    <span>Started {fmt(c.created_at)}</span>
                    <span>Last {fmt(c.last_message_at)}</span>
                    <span>{c.message_count} msg{c.message_count === 1 ? "" : "s"}</span>
                  </div>
                </div>
                <ArrowUpRight size={14} weight="bold" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function TranscriptReader({ conversation, onBack }) {
  const { theme } = useTheme();
  const [messages, setMessages] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get(`/admin/conversations/${conversation.id}/messages`).then(({ data }) => setMessages(data))
      .catch((e) => { setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not load messages"); setMessages([]); });
  }, [conversation.id]);
  return (
    <div className="space-y-3" data-testid="admin-transcript-reader">
      <button className="btn btn-ghost btn-sm" onClick={onBack} data-testid="transcript-back"><ArrowLeft size={14} weight="bold" /> Back to chats</button>
      <div>
        <p className="label-uppercase" style={{ color: "var(--accent)" }}>{conversation.gpt_name} · {conversation.gpt_tab}</p>
        <h3 className="panel-title" data-testid="transcript-title">{conversation.title}</h3>
        <p className="text-xs" style={{ color: "var(--muted)" }}>
          Started {fmt(conversation.created_at)} · {conversation.message_count} messages
          {conversation.deleted_by_user_at && <span className="chip chip-danger ml-2">Deleted by participant · {fmtDate(conversation.deleted_by_user_at)}</span>}
        </p>
      </div>
      {error && <p className="error-banner">{error}</p>}
      {messages === null ? (
        <div className="py-10 flex justify-center"><div className="spinner" /></div>
      ) : messages.length === 0 ? (
        <p className="text-sm py-6 text-center" style={{ color: "var(--muted)" }}>No messages in this conversation yet.</p>
      ) : (
        <ol className="transcript-list">
          {messages.map((m) => (
            <li key={m.id} className={`transcript-msg transcript-${m.role}`} data-testid={`viewer-msg-${m.id}`}>
              <div className="transcript-head">
                <span className="chip chip-muted">{m.role === "user" ? "Participant" : "GPT"}</span>
                <span className="chat-viewer-msg-time">{fmt(m.created_at)}</span>
              </div>
              <div className="transcript-body">
                <MarkdownMessage content={m.content} isDark={theme === "dark"} />
                {m.role === "assistant" && isFullBlueprint(m.content) && (
                  <div className="blueprint-msg-download">
                    <BlueprintDownloadButton messageId={m.id} label="Download Blueprint (PDF)" className="pill-soft" testId={`admin-blueprint-download-${m.id}`} />
                  </div>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
